// Hands the signed-in site session off to the Tiny Award Chrome extension.
// The extension's externally_connectable.matches allows this origin to post
// via chrome.runtime.sendMessage. If the extension isn't installed or the
// handoff fails, we silently give up — the site itself keeps working.

import { apiPost } from '../api/client';
import { SessionUser } from '@tinyrewards/shared';

// Derived from the `key` field in updated Extension/manifest.json.
// If the extension is ever re-keyed, update this constant.
const EXTENSION_ID = 'aalmdggecnmihdnlloomafhmdlnjadgm';

interface ExtensionTokenResponse {
    accessToken: string;
    refreshToken: string;
    user: SessionUser;
}

function hasExtensionApi(): boolean {
    return typeof window !== 'undefined'
        && typeof (window as unknown as { chrome?: { runtime?: { sendMessage?: unknown } } }).chrome !== 'undefined'
        && typeof (window as unknown as { chrome: { runtime?: { sendMessage?: unknown } } }).chrome.runtime !== 'undefined'
        && typeof (window as unknown as { chrome: { runtime: { sendMessage?: unknown } } }).chrome.runtime.sendMessage === 'function';
}

export async function pushAuthToExtension(): Promise<void> {
    if (!hasExtensionApi()) {
        // chrome.runtime is only injected into pages whose origin appears in the
        // extension's externally_connectable.matches. If this fires on a real site
        // origin it means this host is missing from that list, and the handoff is
        // being skipped — the extension then has no access token and every
        // requireJwtOrSession call (CompleteTask, BakeBadge) 401s while the badge
        // panel still renders, because the read endpoints are optional-auth.
        // That exact gap hid dev.everywhere.thinkingcap.com being absent from the
        // list, so log it rather than failing mute.
        console.debug(
            '[extension-bridge] skipping auth handoff: chrome.runtime.sendMessage unavailable on',
            typeof window !== 'undefined' ? window.location.origin : '(no window)',
            '— if the extension is installed, add this origin to externally_connectable.matches in the extension manifest.',
        );
        return;
    }

    let tokens: ExtensionTokenResponse;
    try {
        const res = await apiPost<ExtensionTokenResponse>('/auth/extension-token', {});
        if (!res.success || !res.data) return;
        tokens = res.data;
    } catch {
        return;
    }

    const chrome = (window as unknown as { chrome: { runtime: { sendMessage: (id: string, msg: unknown, cb?: (resp: unknown) => void) => void; lastError?: unknown } } }).chrome;
    try {
        chrome.runtime.sendMessage(
            EXTENSION_ID,
            {
                message: 'version',
                accessToken: tokens.accessToken,
                refreshToken: tokens.refreshToken,
                user: tokens.user,
            },
            () => {
                // Swallow lastError — the user may not have the extension installed.
                void chrome.runtime.lastError;
            },
        );
    } catch {
        // ignore
    }
}
